import useOnMount from '@/hooks/useOnMount';
import { useSetIntercomIsVisible } from '@/queries/layout.queries';
import { Button, Flex } from '@cmpsr/components';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BaseLoadingScreen from '../common/BaseLoadingScreen';
import LoadingMessage from './LoadingMessage';
import { VerticalCopyMarquee } from './VerticalCopyMarquee';

/**
 * Loading screen shown while processing the PPRA results.
 * This is a standalone route that navigates to the results page after loading.
 */
const PPRAResultsLoadingPage: React.FC = () => {
  const navigate = useNavigate();
  const { surveySessionId } = useParams<{ surveySessionId: string }>();
  const setIntercomIsVisible = useSetIntercomIsVisible();
  const messages = [
    "Congratulations! You're about to see the results of your Catch Preliminary Risk Assessment.",
    'Your Preliminary Risk Assessment estimates your lifetime cancer risk using 25 of the 500+ risk factors in the full Catch risk model',
    'Join Catch for $5/month to see your complete lifetime risk for 21 top cancers based on over 500 known risk factors',
    'As a Catch member, you will also receive an optimal Annual Cancer Screening Protocol and Action Plan to reduce your risk and track your progress over time',
    'Catch members can reduce their cancer risk by up to 60% on average',
    'Based on research from leading institutions and data from over 10,000 clinical studies, Catch is using the power of AI to create a new paradigm in cancer: Prevention',
    'Know your cancer risk and take control. Catch.',
  ];
  const MESSAGE_INTERVAL_SECONDS = 5;

  useOnMount(() => {
    setIntercomIsVisible.mutate(false);
    return () => {
      setIntercomIsVisible.mutate(true);
    };
  });

  const handleLoadingComplete = () => {
    navigate(`/free-assessment/results/${surveySessionId}`, { replace: true });
  };

  const StickyButton: React.FC = () => {
    return (
      <Button
        variant="primary"
        size="m"
        width="100%"
        maxWidth="315px"
        height="40px"
        backgroundColor="#7CF1C7"
        color="#183344"
        fontWeight={500}
        fontSize="14px"
        borderRadius="100px"
        onClick={handleLoadingComplete}
      >
        Get Your Preliminary Results
      </Button>
    );
  };

  const componentsAbove = [<LoadingMessage key="loading-message" />];
  const componentsBelow = [
    <Flex alignItems="center" justifyContent="center" key="flex-container" width="100%">
      <Flex width={{ base: '315px', lg: '600px' }}>
        <VerticalCopyMarquee messages={messages} messageIntervalSeconds={MESSAGE_INTERVAL_SECONDS} />
      </Flex>
    </Flex>,
  ];

  return (
    <BaseLoadingScreen
      navBarVariant="dark"
      backButtonVariant="light"
      componentsAboveProgressBar={componentsAbove}
      componentsBelowProgressBar={componentsBelow}
      minimumLoadingSeconds={MESSAGE_INTERVAL_SECONDS * messages.length}
      onLoadingComplete={handleLoadingComplete}
      screenId="ppra-results-loading"
      stickyButton={<StickyButton />}
    />
  );
};

export default PPRAResultsLoadingPage;
